import { Button } from "antd";
import mapboxgl from "mapbox-gl";
import React from "react";
import { MapObject } from "../../../models/MapObject";
import { PdfMapCapturePadding } from "./PdfMapCapture";
import PdfMapView, { PDF_MAP_MAX_ZOOM, PdfMapCamera, pdfMapBounds, PdfMapLogo, zoomForWidth } from "./PdfMapView";

// The share of each edge of the capture that the PDF may crop off, e.g. when the map is printed beside the list.
export interface PdfMapCrop {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface Props {
    mapObjects: MapObject[];
    // What "Fit to items" fits; defaults to everything drawn.
    fitTo?: MapObject[];
    project: any;
    mapStyle: string;
    // The capture's size and fit padding (see PdfMapCapture). The editor is drawn smaller, at the same shape.
    captureWidth: number;
    captureHeight: number;
    padding: PdfMapCapturePadding;
    // The view chosen so far, or null when the map is fitted to its items.
    camera: PdfMapCamera | null;
    crop?: PdfMapCrop;
    showGeotiff?: boolean;
    logo?: PdfMapLogo | null;
    // null puts the map back to fitting its items, which follows them as they're picked.
    onApply: (camera: PdfMapCamera | null) => void;
    onCancel: () => void;
}

const MAX_HEIGHT_RATIO = 0.55;

// A live map, shaped like the printed one, for choosing what the PDF's map shows: move, zoom and turn it, then use that
// view. It's drawn at screen size; the camera is converted to the capture's width so the print shows the same area.
const PdfMapFrameEditor: React.FC<Props> = ({
    mapObjects,
    fitTo,
    project,
    mapStyle,
    captureWidth,
    captureHeight,
    padding,
    camera,
    crop,
    showGeotiff,
    logo,
    onApply,
    onCancel,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const mapRef = React.useRef<mapboxgl.Map | null>(null);
    const [containerWidth, setContainerWidth] = React.useState(0);
    // Whether the view is still the automatic fit, so applying it keeps following the items.
    const [fitted, setFitted] = React.useState(camera === null);

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        const observer = new ResizeObserver(([entry]) => setContainerWidth(Math.floor(entry.contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const aspect = captureWidth / captureHeight;
    const width = Math.floor(Math.min(containerWidth, window.innerHeight * MAX_HEIGHT_RATIO * aspect));
    const height = Math.floor(width / aspect);

    // Whether the logo made it onto the map; one that couldn't be drawn isn't fitted around.
    const logoDrawn = React.useRef(false);

    const fit = (map: mapboxgl.Map) => {
        const bounds = pdfMapBounds(fitTo ?? mapObjects, logoDrawn.current ? logo : null);
        if (bounds.isEmpty()) return;
        const scale = map.getContainer().clientWidth / captureWidth;
        map.fitBounds(bounds, {
            padding: {
                top: padding.top * scale,
                bottom: padding.bottom * scale,
                left: padding.left * scale,
                right: padding.right * scale,
            },
            maxZoom: zoomForWidth(PDF_MAP_MAX_ZOOM, captureWidth, map.getContainer().clientWidth),
            bearing: 0,
            animate: false,
        });
    };

    const onReady = (map: mapboxgl.Map, drawn: boolean) => {
        mapRef.current = map;
        logoDrawn.current = drawn;
        if (camera) {
            map.jumpTo({
                center: camera.center,
                zoom: zoomForWidth(camera.zoom, captureWidth, map.getContainer().clientWidth),
                bearing: camera.bearing,
            });
        } else {
            fit(map);
        }
        // Only a move the user makes (not the framing above) leaves the automatic fit.
        map.on("movestart", (event) => {
            if ("originalEvent" in event && event.originalEvent) setFitted(false);
        });
    };

    const reset = () => {
        if (mapRef.current) fit(mapRef.current);
        setFitted(true);
    };

    const apply = () => {
        const map = mapRef.current;
        if (!map || fitted) {
            onApply(null);
            return;
        }
        const center = map.getCenter();
        onApply({
            center: [center.lng, center.lat],
            zoom: zoomForWidth(map.getZoom(), map.getContainer().clientWidth, captureWidth),
            bearing: map.getBearing(),
        });
    };

    const shade = "absolute bg-gray-900/35 pointer-events-none";

    return (
        <div className="flex flex-col items-center gap-3">
            <div ref={containerRef} className="w-full flex justify-center">
                {width > 0 && (
                    <div
                        className="relative overflow-hidden rounded shadow-md ring-1 ring-gray-200"
                        style={{ width, height }}
                    >
                        <PdfMapView
                            mapObjects={mapObjects}
                            project={project}
                            mapStyle={mapStyle}
                            width={width}
                            height={height}
                            interactive={true}
                            showGeotiff={showGeotiff}
                            logo={logo}
                            onReady={onReady}
                        />
                        {crop && (
                            <>
                                <div className={`${shade} inset-x-0 top-0`} style={{ height: `${crop.top * 100}%` }} />
                                <div
                                    className={`${shade} inset-x-0 bottom-0`}
                                    style={{ height: `${crop.bottom * 100}%` }}
                                />
                                <div className={`${shade} inset-y-0 left-0`} style={{ width: `${crop.left * 100}%` }} />
                                <div
                                    className={`${shade} inset-y-0 right-0`}
                                    style={{ width: `${crop.right * 100}%` }}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
            <p className="m-0 text-xs text-gray-500 text-center">
                Drag to move, scroll to zoom, and right-drag (or Ctrl-drag) to turn the map.
                {crop && " The shaded edges may be cropped to fit the page."}
            </p>
            <div className="flex gap-2">
                <Button onClick={reset} disabled={fitted}>
                    Fit to items
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
                <Button type="primary" onClick={apply}>
                    Use this view
                </Button>
            </div>
        </div>
    );
};

export default PdfMapFrameEditor;
